/**
 * Settings > Service bundles (tracker item 85): the list of bundles with
 * component count, visit span in days and the catalog program total. Office
 * roles manage them; ADMIN also reaches the alert routing from here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IBundlesApi, IItemsApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManageBundleAlerts, canManageBundles } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { primaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { BUNDLE_ALERTS_SETTINGS_PATH, BUNDLE_NEW_PATH, RENEWALS_PATH, bundleEditPath } from '../../features/service-bundles/bundleUi';
import { catalogTotal, catalogTotalText, loadAllItems } from '../../features/service-bundles/bundleItems';

type ListApi = IBundlesApi & Pick<IItemsApi, 'listItems'>;

export function ServiceBundlesPage() {
  const { user } = useAuth();
  const api = useMemo(() => createAPIClient() as ListApi, []);
  const allowed = canManageBundles(user);
  const [bundles, setBundles] = useState<types.ServiceBundle[] | null>(null);
  const [items, setItems] = useState<types.Item[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    api.listServiceBundles()
      .then((list) => { if (active) setBundles(list); })
      .catch((err) => { if (active) { setBundles([]); setError(getErrorMessage(err, 'Could not load the service bundles')); } });
    loadAllItems(api).then((list) => { if (active) setItems(list); }).catch(() => undefined);
    return () => { active = false; };
  }, [api, allowed]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">Service bundles are available to admins and office managers.</p>;

  return (
    <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Service bundles</h1>
        <div className="flex flex-wrap items-center gap-3">
          <Link to={RENEWALS_PATH} className="text-sm font-semibold text-blue-700 hover:underline">Renewals</Link>
          {canManageBundleAlerts(user) && <Link to={BUNDLE_ALERTS_SETTINGS_PATH} className="text-sm font-semibold text-blue-700 hover:underline">Alerts</Link>}
          <Link to={BUNDLE_NEW_PATH} className={`${primaryButtonClass} inline-flex items-center justify-center`}>New bundle</Link>
        </div>
      </div>
      <p className="mb-4 text-sm text-gray-600">A bundle is a program sold again and again, such as spring fertilizer, then weed control 45 days later. Adding one to a property creates every visit as its own job. Editing a bundle never changes visits already created.</p>
      {error && <Toast kind="error">{error}</Toast>}
      {bundles === null ? (
        <p role="status" className="text-sm text-gray-500">Loading service bundles...</p>
      ) : bundles.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-center text-gray-600 shadow">{error ? 'Service bundles could not be loaded.' : 'No service bundles yet. Create one to sell a repeating program.'}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>{['Name', 'Visits', 'Visit span', 'Program total', 'Status'].map((h) => <th key={h} scope="col" className="px-4 py-3 text-left font-medium text-gray-600">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bundles.map((bundle) => (
                <tr key={bundle.id} className="hover:bg-gray-50">
                  <td data-label="Name" data-primary className="px-4 py-3 font-semibold break-words">
                    <Link to={bundleEditPath(bundle.id)} className="text-blue-700 hover:underline">{bundle.name}</Link>
                    {bundle.description && <span className="block text-xs font-normal text-gray-600">{bundle.description}</span>}
                  </td>
                  <td data-label="Visits" className="px-4 py-3">{bundle.componentCount}</td>
                  <td data-label="Visit span" className="px-4 py-3">{bundle.componentCount > 1 ? `${bundle.visitSpanDays} days` : 'One visit'}</td>
                  <td data-label="Program total" className="px-4 py-3">{items.length ? catalogTotalText(catalogTotal(bundle.components, items)) : '...'}</td>
                  <td data-label="Status" className="px-4 py-3">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className={bundle.isActive ? 'text-green-700' : 'text-gray-500'}>{bundle.isActive ? 'Active' : 'Inactive'}</span>
                      {bundle.portalRequestable && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-blue-800">Portal</span>}
                      {bundle.autoRenewDefault && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-700">Auto-renew</span>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
